import ConfirmItsYou from '@/domains/account-management/partials/2fa/confirm-its-you/ConfirmItsYouPartial';
import HavingTroubleError from '@/domains/account-management/partials/2fa/error-screens/HavingTrouble';
import { LoginFormPartial } from '@/domains/account-management/partials/login/LoginFormPartial/LoginFormPartial';
import { useAuthContext } from '@/domains/auth/authContext';
import { useRouter } from 'next/router';
import { useState, useEffect, useRef } from 'react';
import LoadingScreen from '@/domains/shared/components/LoadingScreen';
import { useQueryClient } from '@tanstack/react-query';
import { useAccount } from '@/domains/shared/components/AccountAccess/AccountContext';
import {
  getLoginErrorMessageFromQuery,
  loginErrorMessages,
  LOGIN_ERROR_QUERY_KEY,
} from '@/domains/account-management/partials/login/loginErrorMessages';
import { getDestinationForLogin } from '@/domains/account-management/constants/offerDestinations';
import { setPendingRedirect } from '@/domains/auth/state/pendingRedirect';
import { type AuthErrorCode } from '@/domains/auth/api/responseHelpers/error';

const STEPS = {
  LOGIN: 'LOGIN',
  TWO_FACTOR: 'TWO_FACTOR',
};

const STEP_QUERY_KEY = 'step';
const TWO_FACTOR_STEP_QUERY_VALUE = 'two-factor';

export const LoginFlow = () => {
  const [currentStep, setCurrentStep] = useState(STEPS.LOGIN);

  const [show2faErrorScreen, setShow2faErrorScreen] = useState(false);

  const [emailAddress, setEmailAddress] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState<
    string | React.ReactNode
  >();
  const [otpError, setOtpError] = useState(false);
  const [otpErrorCode, setOtpErrorCode] = useState<AuthErrorCode>();
  const { login, isPending, isSuccess, authReady } = useAuthContext();
  const router = useRouter();
  const [maskedPhoneNumber, setMaskedPhoneNumber] = useState('');
  const { isLoading: isLoadingUserType } = useAccount();

  const [loading, setLoading] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);
  const [codeResent, setCodeResent] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    setLoading(isPending);
  }, [isPending]);

  const routerRef = useRef(router);
  useEffect(() => {
    routerRef.current = router;
  });

  const stepParam = router.query[STEP_QUERY_KEY];

  // The URL drives which step is shown so the 2FA screen has its own URL for
  // observability tooling (e.g. Contentsquare), matching the tabbed-screen
  // pattern used elsewhere. The 2FA step also needs the in-memory credentials,
  // so we fall back to the login form when they're missing (reload/deep-link).
  useEffect(() => {
    if (!router.isReady) return;
    const isTwoFactorStep =
      stepParam === TWO_FACTOR_STEP_QUERY_VALUE && !!emailAddress && !!password;
    setCurrentStep(isTwoFactorStep ? STEPS.TWO_FACTOR : STEPS.LOGIN);

    // Strip a stale 2FA param (e.g. after a refresh with no credentials) so the
    // URL matches the login form that's actually shown.
    if (!isTwoFactorStep && stepParam === TWO_FACTOR_STEP_QUERY_VALUE) {
      const nextQuery = { ...routerRef.current.query };
      delete nextQuery[STEP_QUERY_KEY];
      void routerRef.current.replace(
        { pathname: routerRef.current.pathname, query: nextQuery },
        undefined,
        { shallow: true },
      );
    }
  }, [router.isReady, stepParam, emailAddress, password]);

  const loginErrorParam = router.query[LOGIN_ERROR_QUERY_KEY];

  // Another screen can send the customer here with an error to show, the
  // landing-page login does it when there's no account for the prefilled email.
  // We strip the param once it's on screen so a later attempt doesn't bring the
  // stale message back on refresh.
  useEffect(() => {
    if (!router.isReady) return;
    const message = getLoginErrorMessageFromQuery(loginErrorParam);
    if (!message) return;

    setLoginErrorMessage(message);
    const nextQuery = { ...routerRef.current.query };
    delete nextQuery[LOGIN_ERROR_QUERY_KEY];
    void routerRef.current.replace(
      { pathname: routerRef.current.pathname, query: nextQuery },
      undefined,
      { shallow: true },
    );
  }, [router.isReady, loginErrorParam]);

  if (
    !authReady ||
    loading ||
    isResendingCode ||
    isSuccess ||
    isLoadingUserType
  ) {
    return <LoadingScreen contained />;
  }

  const handleEmailPasswordSubmit = async () => {
    const result = await login(emailAddress, password);

    if (result.resultStatus === 'ERROR') {
      if (result.err === '2FA/TOO-MANY-REQUESTS') {
        setShow2faErrorScreen(true);
        return;
      }
      const message = loginErrorMessages[result.err];
      setLoginErrorMessage(message);
      return;
    }

    setLoginErrorMessage(undefined);
    if (
      result.resultStatus === 'SUCCESSFUL_LOGIN_NO_2FA' ||
      result.resultStatus === 'SUCCESSFUL_LOGIN_2FA'
    ) {
      const offerDestination = getDestinationForLogin(result);
      if (offerDestination) setPendingRedirect(offerDestination);
      await queryClient.invalidateQueries({ queryKey: ['userType'] });
    } else if (result.resultStatus === 'OTP_SENT') {
      setMaskedPhoneNumber(result.validatedMobilePhone);
      setCurrentStep(STEPS.TWO_FACTOR);
      void router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            [STEP_QUERY_KEY]: TWO_FACTOR_STEP_QUERY_VALUE,
          },
        },
        undefined,
        { shallow: true },
      );
    }
    // @TODO - the plugin is not working: https://github.com/cuongpl/capacitor-ios-autofill-save-password/issues/18
    // there is a fork linked in the disccussion that is being maintained - consider switching to that if needed
    // if (Capacitor.getPlatform() === 'ios') {
    //   await SavePassword.promptDialog({
    //     username: emailAddress,
    //     password,
    //   });
    // }
  };
  const handleResendCode = async () => {
    setCodeResent(false);
    setIsResendingCode(true);
    try {
      const result = await login(emailAddress, password);

      if (result.resultStatus === 'OTP_SENT') {
        setMaskedPhoneNumber(result.validatedMobilePhone);
        // Only OTP_SENT means a new code went out, so this is the only path
        // that tells the customer we've sent one.
        setCodeResent(true);
        return;
      }

      if (result.err === '2FA/TOO-MANY-REQUESTS') {
        setShow2faErrorScreen(true);
      }
    } finally {
      setIsResendingCode(false);
    }
  };
  const handleCodeSubmit = async (otp: string, trustDevice: boolean) => {
    const result = await login(
      emailAddress,
      password,
      otp,
      undefined,
      trustDevice,
    );
    const { resultStatus, err } = result;
    if (resultStatus === 'ERROR') {
      if (err === '2FA/INVALID-OTP') {
        setCodeResent(false);
        setOtpErrorCode(err);
        setOtpError(true);
        return;
      } else if (err === '2FA/EXPIRED-OTP') {
        setCodeResent(false);
        setOtpErrorCode(err);
        setOtpError(true);
        return;
      } else if (err === '2FA/TOO-MANY-FAILED-ATTEMPTS') {
        setShow2faErrorScreen(true);
        return;
      }
    }
    if (
      result.resultStatus === 'SUCCESSFUL_LOGIN_NO_2FA' ||
      result.resultStatus === 'SUCCESSFUL_LOGIN_2FA'
    ) {
      const offerDestination = getDestinationForLogin(result);
      if (offerDestination) setPendingRedirect(offerDestination);
      await queryClient.invalidateQueries({ queryKey: ['userType'] });
    }
  };
  if (show2faErrorScreen) {
    return <HavingTroubleError />;
  }
  switch (currentStep) {
    case STEPS.LOGIN:
      return (
        <LoginFormPartial
          emailAddress={emailAddress}
          password={password}
          onEmailChange={setEmailAddress}
          onPasswordChange={setPassword}
          onEmailPasswordSubmit={handleEmailPasswordSubmit}
          errorMessage={loginErrorMessage}
          showPassword={showPassword}
          onChangeShowPassword={setShowPassword}
        />
      );
    case STEPS.TWO_FACTOR:
      return (
        <ConfirmItsYou
          handleOTPSubmission={handleCodeSubmit}
          handleOTPResend={handleResendCode}
          phoneNumber={maskedPhoneNumber}
          errorCode={otpErrorCode}
          otpError={otpError}
          codeResent={codeResent}
          showTrustDevice
        />
      );
    default:
      return null;
  }
};
